/**
 * FHIR client error hierarchy (PROD-642 Sprint 2.2).
 *
 * Every public method on `FhirClient` rejects via one of these classes
 * so callers can `instanceof`-discriminate. Avoid throwing raw `Error`
 * from inside the client; use `wrapUnknownError` to convert unexpected
 * `unknown` failures into a `FhirClientError`.
 *
 * HC5: zero em / en dashes anywhere.
 */

/** Base class for every FHIR client failure. */
export class FhirClientError extends Error {
  constructor(
    message: string,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'FhirClientError';
  }
}

/** Thrown when a resource read does not find the requested id. */
export class FhirNotFoundError extends FhirClientError {
  constructor(
    public readonly resourceType: string,
    public readonly resourceId: string,
    cause?: unknown,
  ) {
    super(
      `FHIR resource not found: ${resourceType}/${resourceId}`,
      cause,
    );
    this.name = 'FhirNotFoundError';
  }
}

/**
 * Thrown when an `If-Match` precondition fails on a write (HTTP 412).
 * Callers should re-fetch the resource and retry the edit.
 */
export class EtagConflictError extends FhirClientError {
  constructor(
    public readonly resourceType: string,
    public readonly resourceId: string,
    public readonly expectedEtag: string,
    cause?: unknown,
  ) {
    super(
      `ETag conflict on ${resourceType}/${resourceId} (expected ${expectedEtag})`,
      cause,
    );
    this.name = 'EtagConflictError';
  }
}

/**
 * Thrown when the requesting `tenantId` does not match the
 * `tenant-id` extension on the resource. Defense-in-depth at the
 * client boundary; the server-side scope filter is the upstream guard.
 */
export class TenantMismatchError extends FhirClientError {
  constructor(
    public readonly resourceType: string,
    public readonly resourceId: string | undefined,
    public readonly requestedTenantId: string,
    public readonly resourceTenantId: string | null,
  ) {
    super(
      `Tenant mismatch on ${resourceType}/${resourceId ?? 'unknown'}: requested "${requestedTenantId}" but resource carries "${resourceTenantId ?? 'none'}"`,
    );
    this.name = 'TenantMismatchError';
  }
}

/** Thrown when authentication fails (token endpoint 4xx / 5xx). */
export class FhirAuthError extends FhirClientError {
  constructor(message: string, cause?: unknown) {
    super(message, cause);
    this.name = 'FhirAuthError';
  }
}

/** Thrown when a write fails Aidbox FHIR Schema validation. */
export class FhirValidationError extends FhirClientError {
  constructor(
    public readonly resourceType: string,
    message: string,
    cause?: unknown,
  ) {
    super(`FHIR validation failed for ${resourceType}: ${message}`, cause);
    this.name = 'FhirValidationError';
  }
}

/** Thrown when a request times out or all retries are exhausted. */
export class FhirTransportError extends FhirClientError {
  constructor(
    message: string,
    public readonly status?: number,
    cause?: unknown,
  ) {
    super(message, cause);
    this.name = 'FhirTransportError';
  }
}

/**
 * Convert an unknown caught value into a `FhirClientError`. Preserves
 * the original cause so callers can inspect the underlying failure.
 */
export function wrapUnknownError(
  context: string,
  err: unknown,
): FhirClientError {
  if (err instanceof FhirClientError) return err;
  const message = err instanceof Error ? err.message : String(err);
  return new FhirTransportError(`${context}: ${message}`, undefined, err);
}
